// BookingForm.jsx
import { useState, useEffect } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import InputText from "../components/InputText";
import Dropdown from "../components/Dropdown/Dropdown";
import Button from "../components/Button";
import instance from "../api/axios";
import mixpanel from "../mixpanel";
import { createUser, getAllTherapist } from "../api/route";
import { validateMobile, validatePinCode } from "../components/validations";
import rightIcon from "../assets/images/right-arrow.png";

import { usePartner } from "../context/PartnerContext";



const BookingForm = ({ formTypeProp, prefilledCity, onCityChange }) => {
  const navigate = useNavigate();
  const { therapistId } = useParams();
  const location = useLocation();
  const { partnerSlug } = useParams();
  const { config } = usePartner() || {};


  // Determine the form type:
  //   If formTypeProp is passed, use it; else infer based on URL
  const formType =
    formTypeProp ||
    (location.pathname.includes("meta-form")
      ? "meta"
      : "normal");

  const cityOptions = ["Mumbai", "Pune", "Gurgaon", "New Delhi", "Noida"];
  const painDurationOptions = ["<2 weeks", "2–6 weeks", "6 weeks–6 months", ">6 months"];
  const [allFieldsFilled, setAllFieldsFilled] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [warnings, setWarnings] = useState({ mobile: "", pinCode: "", pain_condition: "" });
  const [formData, setFormData] = useState({
    user_name: "",
    user_phone: "",
    city: prefilledCity || "Mumbai",
    pinCode: "",
    pain_condition: "",
    pain_score: 0,
    pain_duration: "",
  });
  const [utmParams, setUtmParams] = useState({});

  useEffect(() => {
    if (prefilledCity) {
      setFormData((prev) => ({ ...prev, city: prefilledCity }));
    }
  }, [prefilledCity]);

  // Capture UTM parameters if any.
  useEffect(() => {
    const searchParams = new URLSearchParams(window.location.search);
    const utms = {
      utm_source_custom: searchParams.get("utm_source"),
      utm_medium_custom: searchParams.get("utm_medium"),
      utm_campaign_custom: searchParams.get("utm_campaign"),
      utm_term_custom: searchParams.get("utm_term"),
      utm_addgroup_custom:
        searchParams.get("utm_adgroup") || searchParams.get("utm_adgroup_id"),
      gclid_custom: searchParams.get("gclid"),
      gbraid: searchParams.get("gbraid"),
      wbraid: searchParams.get("wbraid"),
    };
    setUtmParams(utms);
  }, []);

  // Handle input changes and validations
  const handleInputChange = (name, value) => {
    const newFormData = { ...formData, [name]: value };
    let newWarnings = { ...warnings };

    if (name === "pinCode" && value === "111111") {
      newFormData.user_name = "Test";
      newFormData.user_phone = "9429690435";
    }
    if (name === "city") {
      onCityChange?.(value); // 🔁 Sync back to parent
    }

    if (name === "user_phone") {
      newWarnings.mobile = validateMobile(value);
    } else if (name === "pinCode") {
      newWarnings.pinCode = validatePinCode(value);
    }

    setWarnings(newWarnings);
    setFormData(newFormData);
  };

  const checkAllFieldsFilled = () => {
    const filled =
      formData.user_name.trim() !== "" &&
      formData.user_phone.trim() !== "" &&
      formData.city.trim() !== "" &&
      formData.pinCode.trim() !== "" &&
      !warnings.mobile &&
      !warnings.pinCode;

    setAllFieldsFilled(filled);
    console.log("All fields filled (only mandatory fields checked):", filled);
  };


  useEffect(() => {
    checkAllFieldsFilled();
  }, [formData, warnings]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;
    setIsSubmitting(true);

    // ✅ Determine source
    let source = "physica"; // default
    if (sessionStorage.getItem("isFreelance") === "true") {
      source = "freelancing";
    } else if (config?.slug) {
      source = config.slug;
    }

    const submissionType = formType === "meta" ? "meta" : "website";

    // Add the submission and attribution data used by the backend conversion log.
    const userObj = {
      name: formData.user_name,
      address: formData.city,
      pinCode: formData.pinCode,
      phone: formData.user_phone,
      pain_condition: formData.pain_condition,
      pain_score: formData.pain_score,
      pain_duration: formData.pain_duration,
      source,
      submissionType,
      form_submission_timestamp: new Date().toISOString(),
      user_agent: window.navigator.userAgent,
      ad_user_data_consent: "granted",
      ...utmParams,
    };
    console.log("Submitting user object:", userObj);

    try {
      // Submit the user data to create a new user
      const response = await instance.post(createUser, userObj);
      console.log("User created:", response.data);
      const transactionId = response?.data?.transaction_id;
      const savedUser = {
        ...userObj,
        lead_id: response?.data?.lead_id,
        transaction_id: transactionId,
      };
      sessionStorage.setItem("user", JSON.stringify(savedUser));
      const isFreelanceFlow = sessionStorage.getItem("isFreelance") === "true";

      if (source === "physica" && submissionType === "website") {
        if (!transactionId) {
          throw new Error("Backend did not return a booking lead transaction ID");
        }

        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
          event: "booking_lead_saved",
          transaction_id: transactionId,
          value: 200,
          currency: "INR",
        });
        console.log("Booking lead conversion pushed to GTM:", transactionId);
      }

      // Preserve the existing first-party-data event used by the current tags.
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: "enhanced_conversion",
        email: userObj?.email || "", // if you capture email later
        phone_number: userObj?.phone || "",
        first_name: userObj?.name?.split(" ")[0] || "",
        last_name: userObj?.name?.split(" ")[1] || "",
      });
      console.log("✅ Enhanced Conversion data pushed to GTM:", userObj.phone);

      // Mixpanel tracking event
      mixpanel.track("Button Clicked - Sign Up", {
        Name: formData.user_name,
        "Mobile Number": formData.user_phone,
        City: formData.city,
        "Pin Code": formData.pinCode,
      });

      // Fetch therapists and then navigate
      const therapistResponse = await instance.get(
        `${getAllTherapist}?pinCode=${formData.pinCode}`
      );
      console.log("Therapist by pinCode:", therapistResponse.data.therapists);

      if (therapistId) {
        navigate(`/${partnerSlug ? partnerSlug + "/" : ""}therapist-slot/${therapistId}`, {
          state: { ...formData },
        });
      } else {
        if (isFreelanceFlow) {
          navigate(`/${partnerSlug ? partnerSlug + "/" : ""}slot-page-freelancer`, {
            state: { ...formData },
          });
        } else {
          if (
            therapistResponse.data.therapists &&
            therapistResponse.data.therapists.length > 0
          ) {
            navigate(`/${partnerSlug ? partnerSlug + "/" : ""}booking-slot`, {
              state: { ...formData },
            });
          } else {
            navigate(`/${partnerSlug ? partnerSlug + "/" : ""}empty-slot`);
          }
        }
      }

    } catch (error) {
      console.error("Error submitting data:", error);
    } finally {
      setIsSubmitting(false);
    }
  };


  const handleBackButtonClick = () => {
    setFormData({
      user_name: "",
      user_phone: "",
      city: "",
      pinCode: "",
    });
    // Redirect to main website
    window.location.href = "https://www.physica.fit";
  };

  return (
    <form className="w-full flex justify-center bg-transparent px-4" onSubmit={handleSubmit}>
      <style>
        {`
          .loader {
            width: 40px;
            --b: 8px;
            aspect-ratio: 1;
            border-radius: 50%;
            padding: 1px;
            background: conic-gradient(#0000 10%, #FE8235) content-box;
            -webkit-mask:
              repeating-conic-gradient(#0000 0deg, #000 1deg 20deg, #0000 21deg 36deg),
              radial-gradient(farthest-side, #0000 calc(100% - var(--b) - 1px), #000 calc(100% - var(--b)));
            -webkit-mask-composite: destination-in;
                    mask-composite: intersect;
            animation: l4 1s infinite steps(10);
          }
          @keyframes l4 {
            to { transform: rotate(1turn); }
          }
        `}
      </style>
      {isSubmitting && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100vw",
            height: "100vh",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: "rgba(255, 255, 255, 0.2)",
            zIndex: 1000,
            backdropFilter: "blur(2px)",
            flexDirection: "column",
            gap: "12px",
          }}
        >
          <div className="loader" />
          <div style={{ fontSize: "16px", fontWeight: 600, color: "#111827" }}>
            Analyzing Your Pain Information ...
          </div>
        </div>
      )}
      <div className="w-full max-w-[768px]">
        <div className="form_wrapper pt-4">
          <div style={{ fontSize: "14px", color: "black", fontWeight: "600" }}>
            <InputText
              name="user_name"
              label="Name"
              placeholder="Enter Your Name"
              value={formData.user_name}
              onChange={(e) => handleInputChange("user_name", e.target.value)}
            />
            <InputText
              name="user_phone"
              warnings={warnings.mobile}
              label="Mobile Number"
              placeholder="Enter Your Mobile Number"
              value={formData.user_phone}
              onChange={(e) => handleInputChange("user_phone", e.target.value)}
            />
            <Dropdown
              label="City"
              value={formData.city}
              data={cityOptions}
              callback={(e) => handleInputChange("city", e)}
            />
            <InputText
              name="pinCode"
              warnings={warnings.pinCode}
              label="Pin Code"
              placeholder="Enter Your Pin Code"
              value={formData.pinCode}
              onChange={(e) => handleInputChange("pinCode", e.target.value)}
            />
            <InputText
              name="pain_condition"
              warnings={warnings.pain_condition}
              label="Pain Condition"
              placeholder="Describe your pain condition"
              value={formData.pain_condition}
              onChange={(e) => handleInputChange("pain_condition", e.target.value)}
            />
            <Dropdown
              label="How long have you had this pain?"
              value={formData.pain_duration}
              data={painDurationOptions}
              callback={(e) => handleInputChange("pain_duration", e)}
            />

            {/* Pain Score Slider */}
            {/* Pain Score Slider - Simple UI */}
            <div style={{
              marginBottom: "10px", marginTop: "30px", boxSizing: "border-box", marginLeft: "16px", marginRight: "16px",
              width: "90%", padding: "18px", background: "#FFF", borderRadius: "12px", border: "1px solid #E5E7EB"
            }}>
              <label
                style={{
                  fontSize: "14px",
                  color: "black",
                  fontWeight: "600",
                  display: "block",
                  textAlign: "left",
                  // marginBottom: "6px",
                  paddingLeft: "4px",
                }}
              >
                Rate your current pain on 0 to 10
              </label>

              {/* Score Display */}
              <div style={{ textAlign: "center", marginBottom: "0px" }}>
                <div
                  style={{
                    fontSize: "26px",
                    fontWeight: "700",
                    color: "var(--partner-primary)",
                    // marginBottom: "12px",
                  }}
                >
                  {formData.pain_score}
                </div>
              </div>

              {/* Simple Range Slider */}
              <style>
                {`
      .simple-pain-slider {
        -webkit-appearance: none;
        width: 100%;
        height: 6px;
        border-radius: 4px;
        background: linear-gradient(to right, var(--partner-primary) ${formData.pain_score * 10}%, #ccc ${formData.pain_score * 10}%);
        outline: none;
        transition: background 0.3s ease;
      }

      .simple-pain-slider::-webkit-slider-thumb {
        -webkit-appearance: none;
        appearance: none;
        width: 18px;
        height: 18px;
        border-radius: 50%;
        background: var(--partner-primary);
        cursor: pointer;
        border: none;
        box-shadow: 0 0 4px rgba(0,0,0,0.3);
        transition: transform 0.1s ease;
      }

      .simple-pain-slider::-webkit-slider-thumb:hover {
        transform: scale(1.1);
      }

      .simple-pain-slider::-moz-range-thumb {
        width: 18px;
        height: 18px;
        border-radius: 50%;

        background: "var(--partner-primary)";

        cursor: pointer;
        border: none;
        box-shadow: 0 0 4px rgba(0,0,0,0.3);
        transition: transform 0.1s ease;
      }

      .simple-pain-slider::-moz-range-thumb:hover {
        transform: scale(1.1);
      }
    `}
              </style>

              <input
                type="range"
                name="pain_score"
                min="0"
                max="10"
                value={formData.pain_score}
                onChange={(e) => handleInputChange("pain_score", e.target.value)}
                className="simple-pain-slider"
              />

              {/* Labels */}
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  fontSize: "13px",
                  color: "#6B7280",
                  // marginTop: "8px",
                }}
              >
                <span>No Pain</span>
                <span>Worst Pain</span>
              </div>
            </div>



          </div>

          <div className="flex justify-center mt-8">
            {allFieldsFilled ? (
              <Button
                btnName={
                  <span className="flex items-center gap-2">
                    Check Available Slots
                    <img
                      src={rightIcon}
                      alt="arrow"
                      className="w-6 h-5 invert brightness-0"
                    />
                  </span>
                }
                style={{
                  backgroundColor: "var(--partner-primary)",
                  color: "#FFFFFF",
                  fontWeight: 600,
                  fontSize: "18px",
                  textAlign: "center",
                  padding: "10px 40px",
                  borderRadius: "12px",
                  margin: "16px 0",
                  cursor: "pointer",
                  transition: "background-color 0.3s ease, opacity 0.3s ease",
                }}
                disabled={isSubmitting}
              />
            ) : (
              <Button
                btnName={
                  <span className="flex items-center gap-2">
                    Check Available Slots
                    <img
                      src={rightIcon}
                      alt="arrow"
                      className="w-6 h-5 invert brightness-0"
                    />
                  </span>
                }
                style={{
                  backgroundColor: "var(--partner-primary)",
                  color: "#FFFFFF",
                  fontWeight: 600,
                  fontSize: "18px",
                  textAlign: "center",
                  padding: "10px 40px",
                  borderRadius: "12px",
                  margin: "16px 0",
                  opacity: 0.5,
                  cursor: "not-allowed",
                }}
                disabled
              />
            )}

          </div>
        </div>
      </div>
    </form >
  );
};

export default BookingForm;
